import {useCallback, useEffect, useRef, useState} from 'react';
import type {CompositeSignal, StatusFilter} from '../../../../types/compositeSignal';
import {getCompositeSignals} from '../../../../services/compositeSignalService';

const POLL_INTERVAL_MS = 30_000;

interface CompositeConfig {
    symbol: string;
    interval: string;
    autoRefresh: boolean;
    apiKeyId: number | null;
}

const CONFIG_KEY = 'cs_config';

function loadConfig(): Partial<CompositeConfig> | null {
    try {
        const raw = localStorage.getItem(CONFIG_KEY);
        return null == raw ? null : JSON.parse(raw);
    } catch {
        return null;
    }
}

function saveConfig(config: CompositeConfig) {
    try {
        localStorage.setItem(CONFIG_KEY, JSON.stringify(config));
    } catch {
        /* 浏览器禁用存储时仅跳过持久化。 */
    }
}

/**
 * 组合信号列表拉取 + 轮询 hook。
 * - 30s 静默轮询，页面不可见时暂停、回前台立即补拉
 * - 请求序号守卫丢弃乱序旧响应；筛选切换时用 AbortController 取消进行中请求
 */
export function useCompositeSignals(initial: { symbol: string; interval: string }) {
    const saved = loadConfig();
    const [symbol, setSymbol] = useState(initial.symbol);
    const [interval, setIntervalState] = useState(initial.interval);
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('ACTIVE');
    const [autoRefresh, setAutoRefresh] = useState(saved?.autoRefresh ?? true);
    const [signals, setSignals] = useState<CompositeSignal[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [lastUpdated, setLastUpdated] = useState<number | null>(null);

    const seqRef = useRef(0);
    const abortRef = useRef<AbortController | null>(null);
    const apiKeyIdRef = useRef<number | null>(saved?.apiKeyId ?? null);

    const fetchSignals = useCallback(async (silent: boolean) => {
        const seq = ++seqRef.current;
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        if (!silent) setLoading(true);
        try {
            const list = await getCompositeSignals(symbol, interval, 50, statusFilter,
                apiKeyIdRef.current ?? undefined);
            // 乱序守卫：仅接受最新一次请求的结果
            if (seq !== seqRef.current || controller.signal.aborted) return;
            setSignals(list);
            setError(null);
            setLastUpdated(Date.now());
        } catch (e) {
            if (seq !== seqRef.current || controller.signal.aborted) return;
            setError(e instanceof Error ? e.message : '加载失败');
        } finally {
            if (seq === seqRef.current && !controller.signal.aborted) setLoading(false);
        }
    }, [symbol, interval, statusFilter]);

    // symbol/interval/筛选变化立即刷新
    useEffect(() => {
        fetchSignals(false);
        saveConfig({symbol, interval, autoRefresh, apiKeyId: apiKeyIdRef.current});
        return () => abortRef.current?.abort();
    }, [fetchSignals, symbol, interval, autoRefresh]);

    // 轮询：可见性暂停恢复
    useEffect(() => {
        if (!autoRefresh) return;
        const timer = window.setInterval(() => {
            if ('visible' === document.visibilityState) fetchSignals(true);
        }, POLL_INTERVAL_MS);
        const onVisible = () => {
            if ('visible' === document.visibilityState) fetchSignals(true);
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            window.clearInterval(timer);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [autoRefresh, fetchSignals]);

    const setApiKeyId = useCallback((id: number | null) => {
        apiKeyIdRef.current = id;
        saveConfig({symbol, interval, autoRefresh, apiKeyId: id});
    }, [symbol, interval, autoRefresh]);

    return {
        symbol, setSymbol,
        interval, setIntervalState,
        statusFilter, setStatusFilter,
        autoRefresh, setAutoRefresh,
        signals, loading, error, lastUpdated,
        refresh: () => fetchSignals(true),
        reload: () => fetchSignals(false),
        apiKeyId: apiKeyIdRef.current, setApiKeyId,
    };
}
